import './ActiveTablesExpertMode.styles.scss';

import _ from 'lodash';
import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { useDebounce } from 'primereact/hooks';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Permission } from '../../../model/enumeration/Permission.enum';
import authStore from '../../../store/AuthStore';
import availableBotStore from '../../../store/AvailableBotStore';
import botErrorStore from '../../../store/BotErrorStore';
import chatStore from '../../../store/ChatStore';
import commonStore from '../../../store/CommonStore';
import permissionStore from '../../../store/PermissionStore';
import playerStore from '../../../store/PlayerStore';
import sessionStorageStore from '../../../store/SessionStorageStore';
import { GameTable } from '../../../store/state/GameTable';
import tableStore from '../../../store/TableStore';
import userSettingsStore from '../../../store/UserSettingsStore';
import { getTableColor } from '../../../utils/game-types.utils';
import { copyToClipboard } from '../../../utils/keyboard.utils';
import BotErrorWindow from '../../BotErrorWindow/BotErrorWindow';
import Filter from '../../Filter/Filter';
import LongText from '../../LongText/LongText';
import { Paths } from '../../Router/Paths';
import Settings from '../../Settings/Settings';
import ActiveTablesSorting from '../../shared/ActiveTablesSorting/ActiveTablesSorting';
import ButtonWithChat from '../../shared/Button/ButtonWithChat/ButtonWithChat';
import DashboardExpertMode from '../DashboardExpertMode/DashboardExpertMode';

const SEARCH_VALUE_STORAGE_KEY = 'search-value-expert-mode';

const ActiveTablesExpertMode: FC = () => {
  const [toggleSearchEvent, setToggleSearchEvent] =
    useState<React.MouseEvent<HTMLSpanElement>>();
  const navigate = useNavigate();
  const [searchValue, debouncedSearchValue, setSearchValue] = useDebounce(
    availableBotStore.searchFilter.searchName,
    400,
  );

  const filteredTablesSorted = useMemo(
    () => tableStore.getFilteredTablesSorted(),
    [
      tableStore.activeTables.size,
      tableStore.filterStateChanged,
      tableStore.selectedFilterOptions,
      tableStore.activeTablesSorting,
      tableStore.activeTablesSorting.size,
      commonStore.isExpertModeEnabled,
      tableStore.searchValueExpertMode,
    ],
  );

  const hasManageBotsSeatingPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS_SEATING),
    [authStore.authenticatedUser],
  );

  const isFilterSelected = useMemo(() => {
    return !_.isEqual(
      tableStore.selectedFilterOptions,
      tableStore.defaultSelectedFilterOptions,
    );
  }, [tableStore.filterStateChanged, tableStore.selectedFilterOptions]);

  useEffect(() => {
    if (
      !userSettingsStore.settings.chat.disabled &&
      !commonStore.backgroundMode
    ) {
      void chatStore.initActiveChatMessages();
    }
  }, [commonStore.backgroundMode]);

  useEffect(() => {
    const tableSearchValue = sessionStorageStore.getSearchValueExpertMode(
      SEARCH_VALUE_STORAGE_KEY,
    );
    if (tableSearchValue) {
      tableStore.setSearchValueExpertMode(tableSearchValue);
      setSearchValue(tableSearchValue);
    }
  }, []);

  useEffect(() => {
    tableStore.setSearchValueExpertMode(debouncedSearchValue);
    sessionStorageStore.setSearchValueExpertMode(
      SEARCH_VALUE_STORAGE_KEY,
      debouncedSearchValue,
    );
  }, [debouncedSearchValue]);

  const getToggleBotErrorEvent = useCallback(
    (tableId: string) => botErrorStore.toggleBotErrorEventMap.get(tableId),
    [botErrorStore.toggleBotErrorEventMap.size],
  );
  const onLogout = (): Promise<void> =>
    authStore.logout().then(() => navigate(Paths.LOGIN));

  const addTable = useCallback(
    (tableId: string): void => {
      tableStore.viewingTableIds.length === 1
        ? tableStore.replaceViewingTableExpertMode(tableId)
        : tableStore.addViewingTable(tableId);
    },
    [tableStore.viewingTableIds.length],
  );

  const onCopyTableName = useCallback(
    (table: GameTable): void => {
      addTable(table.id);
      void copyToClipboard(table.name);
    },
    [addTable],
  );

  return (
    <div className="ActiveTablesExpertMode-Component">
      <div>
        <div className="header-expert-mode">
          <div className="header-title">Expert mode</div>
          <div className="header-settings">
            <div className="header-filter">
              <Button
                className={`filter-btn${isFilterSelected ? ' selected' : ''}`}
                icon="pi pi-search"
                severity="secondary"
                tooltip="Filter"
                text
                rounded
                onClick={setToggleSearchEvent}
              ></Button>
              <Filter toggleEvent={toggleSearchEvent} isChipsVisible={false} />
            </div>
            <div className="header-actions">
              <DashboardExpertMode />
              <Settings />
              <Button
                className="logout-btn"
                tooltip="Logout"
                icon="pi pi-sign-out"
                rounded
                text
                severity="secondary"
                onClick={onLogout}
                aria-label="logout"
              />
            </div>
          </div>
        </div>
        <div className="sort-properties">
          <IconField iconPosition="right" className="search-field">
            <InputIcon
              className="pi pi-times cursor-pointer"
              onClick={() => setSearchValue('')}
            />
            <InputText
              value={searchValue}
              onChange={e => setSearchValue(e.target.value)}
              placeholder="Enter pid or nick"
              className="search-value-input"
            />
          </IconField>
          <ActiveTablesSorting />
        </div>
      </div>
      <div className="list">
        {filteredTablesSorted.map(table => (
          <div
            key={table.id}
            className={`table${
              tableStore.isTableViewing(table.id) ? ' active-table' : ''
            }`}
            onClick={() => addTable(table.id)}
          >
            <div className="table-header">
              <div
                className="table-game-type"
                style={{
                  backgroundColor: getTableColor(table.currentGameType),
                }}
              >
                {table.gameType}
              </div>
              <div className="table-name">
                <LongText
                  text={table.name}
                  onCopyText="Table name copied to clipboard"
                  onCopyData={table}
                  onCopy={onCopyTableName}
                />
              </div>
            </div>
            <div className="limits">
              <p>{table.bb}</p>
            </div>
            <div className="table-stats">
              <div
                className="table-chat-buttons"
                onClick={e => e.stopPropagation()}
              >
                <ButtonWithChat tableId={table.id} expertMode={true} />
                <ButtonWithChat
                  tableId={table.id}
                  expertMode={true}
                  emoji={true}
                />
              </div>
              {hasManageBotsSeatingPermission && (
                <div className="table-stats-item">
                  <i className="pi pi-calendar-clock stat-icon" />
                  <span>{table.waitlistCount}</span>
                </div>
              )}
              <div className="table-stats-item">
                <i className="pi pi-eye stat-icon" />
                <span>{table.viewersCount}</span>
              </div>
              <div className="table-stats-item">
                <img
                  className="stat-icon"
                  src="/assets/bot-stats.svg"
                  alt="bots"
                  width="22"
                  height="22"
                />
                <span>
                  {table.players.filter(player => player?.myBot).length}
                </span>
              </div>
              <div className="table-stats-item">
                <img
                  className="stat-icon"
                  src="/assets/player-stats.svg"
                  alt="players"
                  width="22"
                  height="22"
                />
                <span>{playerStore.findLivePlayersCount(table)}</span>
              </div>
            </div>
            <BotErrorWindow
              key={`bot-errors-${table.id}`}
              table={table}
              toggleEvent={getToggleBotErrorEvent(table.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default observer(ActiveTablesExpertMode);
